/**
 * The tmux view's wire format: the claude user's tmux sessions, relayed.
 *
 * Three hops. The browser talks to todos (`/ws/tmux`, admins only); todos talks
 * to the tmux bridge (`runner/tmux.ts`, running as `claude`) over one socket at
 * `/api/runner/tmux`; the bridge runs `tmux attach` in a pty. todos never runs
 * tmux itself — it runs as `bob`, and the sessions are not its to reach.
 */
import type { WorkerRoom, WorkerTree } from "./workers.js";
import { blockedAncestor, CLAUDE_PROMPT_MAX, worktreeNameFor, type ClaudeModel, type DiskUsage, type Status, type Task } from "./types.js";
import { reaches, trunkOf, type GitGraph, type GitWorktree } from "./gitGraph.js";
import type { RepoDiff } from "./diff.js";
import type { DirListing, RepoFile } from "./files.js";
import type { ClaudeMode, PaneBackground, Transcript } from "./transcript.js";
import { lessEager, type StartMode } from "./startMode.js";
import { AUTO_MERGE_ID, STAGING_BRANCH, heldByDeploy, isFixWorktree, mergeBranchOf } from "./repoSettings.js";

/** Where a browser opens one terminal: `?session=<name>&cols=&rows=`. */
export const TMUX_WS_PATH = "/ws/tmux";
/** Where the bridge connects, with the runner's bearer token. */
export const TMUX_BRIDGE_PATH = "/api/runner/tmux";

export interface TmuxSession {
  /** On a worker, `<worker>.<its own name>` (shared/workers.ts `qualify`): the server qualifies it. */
  name: string;
  /** The worker it runs on, by name; absent on this box's. Set by the server, never a bridge. */
  host?: string;
  windows: number;
  /** How many clients are attached, this view's included. */
  attached: number;
  createdAt: number;
  /** The last key a client sent it. */
  activityAt: number;
  /** The last output in any of its windows, whatever printed it, or 0. */
  outputAt: number;
  /** Printed something in the last few seconds (`BUSY_MS`): a build, a tail -f.
   *  A Claude in it says what it is doing in `claude.state`, which the view
   *  believes over this: its spinner prints, and so does a redraw at its prompt. */
  busy: boolean;
  /**
   * The repo whose checkout the session's active pane is in (a worktree under
   * it counts), or null. The bridge checks it is a git checkout; todos cannot,
   * since the server's unit has no /home (ProtectHome).
   */
  repo: string | null;
  /** That repo's checkout — the one its command buttons run in, not a worktree
   *  — has uncommitted changes, untracked files included. False with no repo. */
  dirty: boolean;
  /** The session's own worktree has uncommitted changes. False with no worktree:
   *  a Claude in a worktree commits there, so the checkout's state says nothing
   *  about its work. */
  worktreeDirty: boolean;
  /** The session's worktree has commits `STAGING_BRANCH` (or, before there is
   *  one, the branch its repo's checkout has out) lacks: what "Merge into
   *  staging" would take. False with no worktree, or once
   *  it is all in; absent from an older bridge, or when git could not say. */
  worktreeAhead?: boolean;
  /** The other way round: staging has commits the worktree lacks — what "Catch
   *  up staging" would take. Same falses and absences as `worktreeAhead`. */
  worktreeBehind?: boolean;
  /** Everything committed in the session's worktree is in the checkout's
   *  `main`, and nothing is left uncommitted: its work has shipped, or there is
   *  none yet. Absent with no worktree, from an older bridge, or when git
   *  could not say. */
  worktreeInMain?: boolean;
  /** The task a Claude button started it for, or null — kept on the session
   *  itself (`@todos_task`), so a reload or another tab still knows. */
  taskId: string | null;
  /** That task's title, for the view's label — added by todos, which has the
   *  tasks, and only for one the reader can see. Titles change, so it is looked
   *  up on every list rather than kept on the session. */
  taskTitle?: string | null;
  /** With no `taskId` (a terminal, a Claude started by hand), the task its worktree's
   *  Claude button was started for (`worktree_tasks`), where the reader may see
   *  it — added by todos for the view's header only. Never read it as the
   *  session's task: auto-commit and the rest go by `taskId`. */
  worktreeTaskId?: string | null;
  /**
   * The Claude Code worktree the session is working in, or null: the name the
   * Claude button was given, else the one the active pane's path is under
   * (`<repo>/.claude/worktrees/<name>`).
   */
  worktree: string | null;
  /** Where the session is working, for the view: the active pane's directory
   *  (`pane_current_path`), or its worktree when Claude Code is working in one
   *  from the checkout. Home is written `~`. "" if tmux did not say. */
  cwd: string;
  /** What the Claude Code running in it has spent, or null when none is.
   *  Absent from a bridge older than this. */
  claude?: ClaudeStats | null;
  /** When the command a button or the exec box started in it finished, in ms,
   *  or null while it runs or for any other session. Absent from an older bridge. */
  ranAt?: number | null;
  /** That command's exit status, set with `ranAt`. Absent from an older bridge. */
  ranStatus?: number | null;
  /** The repo command a button started it with (`RunCommandRequest.commandId`),
   *  kept on the session itself, so every tab and the server can tell a Deploy
   *  is still running (`runningDeploy`) — through a restart of todos, which a
   *  deploy of todos is. Null for any other session; absent from an older bridge. */
  command?: { repo: string; id: string } | null;
  /** While a Deploy runs, the last step its script announced (`[deploy] …`,
   *  `lastDeployStep`), read off the pane, or null before the first. Absent for
   *  any other session and from an older bridge. */
  deployStep?: string | null;
  /** A command that FAILED (`ranStatus` not 0): the last lines it printed
   *  (`commandFailure`), read off the pane by the bridge, so what went wrong is
   *  still said once its session has been closed. Absent otherwise, and from an
   *  older bridge. */
  failure?: string | null;
  /** A Deploy that has taken staging into main (`STAGING_MERGED_OPTION`), so
   *  merges into staging no longer wait for it (`deployHolding`). Absent for
   *  any other session and from an older bridge. */
  stagingMerged?: boolean;
  /** What its Claude runs in the background — shells, monitors, agents — read
   *  off the footer under its prompt box (`paneBackground`), for the pill's
   *  status line. Null without a Claude, or while a dialog hides the footer;
   *  absent from an older bridge. */
  background?: PaneBackground | null;
}

/**
 * What a Claude Code is doing: in a turn, stopped on a question only a person
 * can answer (a permission dialog), or at its prompt. Told by its own hooks
 * (`claudeStateHooks` in runner/claudeStats.ts), not guessed from its output.
 */
export type ClaudeState = "working" | "waiting" | "idle";

/** A Claude Code's spend so far, read from its transcript (runner/claudeStats.ts). */
export interface ClaudeStats {
  /** Absent from a bridge older than this. */
  state?: ClaudeState;
  /** Every token it has written, thinking included, over the conversation. */
  outputTokens: number;
  /** The thinking part of `outputTokens`. */
  thinkingTokens: number;
  /** What its last request sent: the conversation's current size. */
  contextTokens: number;
  /** Time spent on finished turns, by Claude Code's own count. */
  workedMs: number;
  /** How long the turn in progress has run, or null between turns. */
  turnMs: number | null;
  /** What it is at: its last tool call in a line (`toolDoing`, runner/claudeStats.ts),
   *  or null when its last word was text, thinking or a person's prompt. Read off the
   *  transcript whatever `state` says, so between turns it is what it did last.
   *  Absent from an older bridge. */
  doing?: string | null;
}

/**
 * A Claude session the modal queued instead of starting: listed under the
 * sessions, started only when someone presses Send. Shared by every admin,
 * like the sessions themselves.
 */
export interface QueuedClaude {
  id: string;
  taskId: string;
  /** Only where the reader may see the task, as with `TmuxSession.taskTitle`. */
  taskTitle: string | null;
  /** Its task's status. `blocked` holds it: no start mode sends it
   *  (`startsOnItsOwn`, server/usageStart.ts) — only a Send by hand. */
  taskStatus: Status | null;
  /** A task above it is `blocked` ("blocked by ancestor"): held just as a
   *  blocked task is. Absent (an older copy) is false. */
  ancestorBlocked?: boolean;
  /** The least eager start mode of the queued tasks above it, and whose
   *  (`queuedAncestorMode`): it goes no more readily than that (`startModeOf`).
   *  Null or absent (an older copy): none above it is queued. */
  ancestorMode?: AncestorMode | null;
  /** Its repo's start limit (`RepoSettings.startLimit`): it goes no more readily
   *  than that either (`startModeOf`). Null or absent: no limit. */
  repoMode?: StartMode | null;
  repo: string | null;
  prompt: string;
  /** `prompt` was written for this task (the queued view's Override), so its
   *  notes no longer refill it from the brief. Absent (an older copy) is false. */
  promptOwn?: boolean;
  worktree: string | null;
  /** The branch its worktree is made from and auto-merged into once sent
   *  (`mergeBranchProblem`), made from staging if there is none yet. Null is
   *  staging. Absent (an older copy) is null. */
  branch?: string | null;
  model: ClaudeModel;
  /** Started in Claude Code's plan mode (`--permission-mode plan`): it writes a
   *  plan and waits for a person to approve it before changing anything.
   *  Absent (an older copy) is false. */
  plan?: boolean;
  /** With `plan`: "plan, then auto" — its plan is approved by itself (a
   *  `PermissionRequest` hook on `ExitPlanMode`) and it goes on in auto mode.
   *  The plan stays in its conversation, to be revised from later (the Chat's
   *  Revise plan). Absent is false. */
  planAuto?: boolean;
  createdAt: number;
  /** The Claude worktrees it waits for (`waitingOn`): picked from their pills
   *  in the git panel, and its Send is refused until each is done. */
  dependsOn: string[];
  /** Other queued requests it waits for, by id: until each is sent. Sending
   *  one hands its worktree on to `dependsOn`, so the wait carries on until
   *  that work is on staging; removing one drops it from here. It also holds
   *  the ids of `dependsOnTasks`, so a list sent back whole keeps them. */
  dependsOnQueued: string[];
  /** Tasks NOT on the queue it waits for (hidden from Claude, an admin task,
   *  one being done by hand), picked from the git panel's "ancestors" rows:
   *  until each is finished (`taskFinished`). Only the unfinished ones are
   *  listed. Absent (an older copy) is none. */
  dependsOnTasks?: TaskWait[];
  /** What of `dependsOn` and `dependsOnQueued` it waits on only because a task
   *  above it (todo or blocked) does — "auto improve" under "phase 2" waits on
   *  whatever phase 2 waits on. Listed in those two as well, so every reader
   *  holds it; a save strips these, so they stay the ancestor's, never its own.
   *  Absent: none. */
  inheritedDepends?: { dependsOn: string[]; dependsOnQueued: string[] };
  /** The status of the task each of `dependsOn` was started for
   *  (`worktree_tasks`), where the reader may see it. A worktree whose task is
   *  not yet `review` or `done` is waited on however its git looks
   *  (`waitingOn`): one removed before its work reached staging reads as done. */
  dependsOnStatus: Record<string, Status>;
  /** How it starts (shared/startMode.ts). `ready` goes once nothing holds it
   *  (server/autoStart.ts) — a dependant too: waiting on something only says when, never that it should go
   *  (`startsOnItsOwn`). `under` / `wastage` go by the plan usage
   *  (`usageSends`, server/usageStart.ts), as whoever picked them. */
  startMode: StartMode;
  /** Its task's own urgency (URGENCY_MIN..URGENCY_MAX): the last thing the
   *  queue sorts by before age, and the order an automatic start takes rows
   *  that are equally ready (`listQueue` sends them most urgent first).
   *  Absent (an older copy) is URGENCY_MIN. */
  urgency?: number;
  /** Who queued it: an automatic Send starts it as them. Null once they are gone. */
  createdBy: string | null;
  /** On an Under row: what the Under sends are still at work on, once no more
   *  may go (`underHolder()`, server/underRuns.ts), which holds every other
   *  Under row and prompt outside wastage. Null when none; absent (not Under,
   *  or an older copy) is none. */
  underHeldBy?: UnderHolder | null;
}

/** What holds the Under rows and prompts, once as many are at work as the usage
 *  allows (`slots`: `underSlots()`, shared/usage.ts): the oldest of them — a
 *  task sent Under still `doing`, or a prompt sent Under not yet over
 *  (`promptId`, its `worktree`, and that worktree's task if one is recorded).
 *  A title only where the reader may see it. */
export interface UnderHolder {
  taskId: string | null;
  taskTitle: string | null;
  promptId?: string;
  worktree?: string;
  /** How many may be at work at once just now. Absent (an older copy) is one. */
  slots?: number;
  /** The points under the week's pace that let one more go (the Global
   *  settings' "Under runs"); null is only ever one. */
  step?: number | null;
}

/** A task off the queue that a queued request or prompt waits for
 *  (`dependsOnTasks`). The title only where the reader may see it. */
export interface TaskWait {
  id: string;
  title: string | null;
  status: Status;
}

/** Whether a task waited on as a task (`dependsOnTasks`) is over: done, or in
 *  review too while the repo's Start at review is on — the rule a worktree's
 *  task is judged by (`waitingOn`). */
export const taskFinished = (status: Status, atReview: boolean): boolean => status === "done" || (atReview && status === "review");

/** Anything in the queue that others may wait on: a queued Claude, or a
 *  worktree's queued prompt. Their ids never collide (`newId()`), so one
 *  `dependsOnQueued` list names either. */
export type QueueNode = Pick<QueuedClaude, "id" | "dependsOnQueued">;

/** Which of a request's queued dependencies are still queued. One that is not
 *  in the list was sent (and is in `dependsOn` now) or removed. Pass the
 *  prompts too, or a wait on one is not seen. */
export function waitingOnQueued<T extends { id: string }>(q: QueueNode, queue: readonly T[]): T[] {
  return q.dependsOnQueued.flatMap((id) => queue.find((o) => o.id === id && o.id !== q.id) ?? []);
}

/** How a queued request is named in another's dependencies: its worktree,
 *  which is what it becomes once sent, else its task. A prompt: its worktree
 *  and how it begins. */
export function queuedLabel(q: QueuedClaude | QueuedPrompt): string {
  if (!("taskId" in q)) return `${q.worktree}: ${q.prompt.slice(0, 30)}`;
  return q.worktree ?? q.taskTitle ?? q.prompt.slice(0, 40);
}

/** True if `from` waiting on `to` would close a loop: `to` already waits,
 *  however indirectly, on `from`. Such a pair could never be sent. */
export function queueCycle(from: string, to: string, queue: readonly QueueNode[]): boolean {
  const seen = new Set<string>();
  const stack = [to];
  while (stack.length) {
    const id = stack.pop()!;
    if (id === from) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    stack.push(...(queue.find((q) => q.id === id)?.dependsOnQueued ?? []));
  }
  return false;
}

/** How many worktrees one queued request may wait on. */
export const QUEUE_DEPENDS_MAX = 20;

/** A dependency's task in one of these has had its work finished: `review` is
 *  what a merge into staging sets (server/stagingReview.ts) — unless the branch
 *  the waiting row goes off has "Start at review" off (`RepoBranch.startAtReview`):
 *  then only `done`. */
export const DEPENDENCY_DONE: readonly Status[] = ["review", "done"];
export const dependencyDone = (atReview: boolean): readonly Status[] => (atReview ? DEPENDENCY_DONE : ["done"]);

/** "Start at review" for a repo: one answer, or each branch's (`startsAtReview`),
 *  asked with the branch a row goes off (`queuedBase`). */
export type AtReview = boolean | ((branch: string) => boolean);
export const atReviewOn = (atReview: AtReview, branch: string): boolean =>
  typeof atReview === "function" ? atReview(branch) : atReview;

/**
 * Which of `dependsOn` a queued request is still waiting for: a worktree whose
 * task (`statuses`, by worktree) is not yet `review` or `done` (only `done`
 * without `atReview`, "Start at review" on the branch it goes off), or one that
 * holds uncommitted work, or commits staging (main before there is one) lacks —
 * what `ahead` counts. One that is gone counts as done as far as git goes
 * once its branch is gone too or on staging (`unmerged`, `GitGraph.unmerged`):
 * Delete and Done refuse a dirty tree, but keep an unmerged branch — a task set
 * done before auto-merge took its work leaves one — and its task still has to
 * say so. Without the worktree list (an older bridge) nothing can be told, so
 * every one waits. Its own worktree is never waited on; one with no task
 * recorded goes by git alone.
 */
export function waitingOn(
  dependsOn: readonly string[],
  worktrees: readonly GitWorktree[] | undefined,
  own: string | null = null,
  statuses: Readonly<Record<string, Status>> = {},
  atReview = true,
  unmerged: readonly string[] = [],
): string[] {
  const deps = dependsOn.filter((d) => d !== own);
  if (!worktrees) return deps;
  const finished = dependencyDone(atReview);
  return deps.filter((d) => {
    const status = statuses[d];
    if (status && !finished.includes(status)) return true;
    const w = worktrees.find((t) => t.name === d);
    if (!w) return unmerged.includes(`worktree-${d}`);
    return w.dirty !== false || w.ahead !== 0;
  });
}

/**
 * Whether server/autoStart.ts sends it once nothing holds it: started `ready`.
 * A dependant is no exception: what it waits on says when it may go, its start mode
 * whether it goes by itself at all. Never while its
 * task, or any task above it, is `blocked`, which says it should not go at all yet.
 */
export function startsOnItsOwn(q: QueuedClaude): boolean {
  if (q.taskStatus === "blocked" || q.ancestorBlocked) return false;
  return startModeOf(q) === "ready";
}

/** A start mode rolled up from above: the queued task above a row (or, for a
 *  prompt, its worktree's task and above) whose start mode goes least readily. */
export interface AncestorMode {
  mode: StartMode;
  taskId: string;
}

/**
 * The least eager start mode among the queued tasks above `taskId` — `self`:
 * from that task itself (a prompt's worktree's task, if queued again) — the
 * nearest of equals; null when none above is queued. `modeOf` is each queued
 * task's own start mode, undefined for one not on the queue, whose mode means
 * nothing. A Manual parent holds every queued row under it ("ancestor manual").
 * Guards against a parent cycle, as `blockedAncestor` does.
 */
export function queuedAncestorMode(
  taskId: string,
  get: (id: string) => Pick<Task, "parentId"> | null | undefined,
  modeOf: (id: string) => StartMode | undefined,
  self = false,
): AncestorMode | null {
  const seen = new Set<string>(self ? [] : [taskId]);
  let id: string | null = self ? taskId : (get(taskId)?.parentId ?? null);
  let found: AncestorMode | null = null;
  while (id && !seen.has(id)) {
    seen.add(id);
    const mode = modeOf(id);
    if (mode && (!found || lessEager(mode, found.mode))) found = { mode, taskId: id };
    id = get(id)?.parentId ?? null;
  }
  return found;
}

/** What `startModeOf` reads. */
export interface ModeHolder {
  startMode: StartMode;
  ancestorMode?: AncestorMode | null;
  repoMode?: StartMode | null;
}

/** Its own start mode, or a queued ancestor's when that goes less readily. */
const ownOrAncestor = (n: ModeHolder): StartMode =>
  n.ancestorMode && lessEager(n.ancestorMode.mode, n.startMode) ? n.ancestorMode.mode : n.startMode;

/** How it really starts: its own start mode, a queued ancestor's when that goes
 *  less readily (`ancestorMode`), or its repo's start limit when that does
 *  (`repoMode`). What every sender goes by; the dropdown still shows and sets its own. */
export const startModeOf = (n: ModeHolder): StartMode => {
  const mode = ownOrAncestor(n);
  return n.repoMode && lessEager(n.repoMode, mode) ? n.repoMode : mode;
};

/** The repo's start limit when it is what holds it back, or null. */
export const modeFromRepo = (n: ModeHolder): StartMode | null =>
  n.repoMode && lessEager(n.repoMode, ownOrAncestor(n)) ? n.repoMode : null;

/** The ancestor holding it to a less eager start mode than its own, or null. */
export const modeFromAncestor = (n: { startMode: StartMode; ancestorMode?: AncestorMode | null }): AncestorMode | null =>
  n.ancestorMode && lessEager(n.ancestorMode.mode, n.startMode) ? n.ancestorMode : null;

/**
 * Whether server/promptQueue.ts sends a queued prompt by its start mode, as a
 * queued Claude's goes: Ready once nothing holds it, Under or Wastage when the
 * plan usage says so (`byUsage`, the server's `usageSends`), Manual never — its
 * own, or a queued ancestor's that goes less readily (`startModeOf`). A prompt
 * has no task, so nothing blocks it. What it waits on, and the repo's holds, are
 * judged apart.
 */
export function promptGoes(
  p: Pick<QueuedPrompt, "startMode" | "ancestorMode" | "repoMode">,
  byUsage: (mode: "under" | "wastage") => boolean,
): boolean {
  const mode = startModeOf(p);
  if (mode === "ready") return true;
  return mode === "under" || mode === "wastage" ? byUsage(mode) : false;
}

/**
 * What holds a queued request as `blocked` (`startsOnItsOwn`): its own task, a
 * task above it ("blocked by ancestor"), or nothing. `get` is the page's live
 * store, read first; a task it does not hold falls back to what the server said.
 */
export function queuedBlockedBy(q: QueuedClaude, get?: (id: string) => Task | undefined): "task" | "ancestor" | null {
  const task = get?.(q.taskId);
  if ((task?.status ?? q.taskStatus) === "blocked") return "task";
  if (task && get ? blockedAncestor(task, get) : q.ancestorBlocked) return "ancestor";
  return null;
}

/** How a queued request's dependency list names the wait on auto-fix: while a
 *  failed Deploy has a Claude fixing it (`fixingWorktree`, shared/repoSettings.ts),
 *  nothing queued in that repo is sent — by hand or on its own — so new work does
 *  not land on a staging that does not deploy. Over once the fix is merged into
 *  staging, found nothing to fix, or auto-fix is unticked. */
export const WAITING_FOR_FIX = "Waiting for fix";

/** How a queued request's dependency list names the wait on a merge that is
 *  moving staging right now (`MOVES_STAGING`): nothing is sent ON ITS OWN —
 *  Ready, Under, Wastage — until it ends, since a new worktree is branched at
 *  staging and so waits to be branched at the result. A Send by hand still
 *  goes. A running Deploy holds nothing: it moves main, not staging. */
export const WAITING_FOR_STAGING = "Waiting for staging";

/** The commands that move staging while they run: a worktree's Merge into
 *  staging, auto-merge, the staging pill's two merges and its Pull, and a branch pill's. */
const MOVES_STAGING: ReadonlySet<string> = new Set(["merge-into", AUTO_MERGE_ID, "staging-into-main", "main-into-staging", "merge-branch", "merge-link", "pull-staging"]);

/** What holds everything queued in `repo` from going on its own, from the
 *  session list: a merge moving staging, else null. */
export function repoHold<S extends Pick<TmuxSession, "command" | "ranAt">>(
  sessions: readonly S[],
  repo: string,
): typeof WAITING_FOR_STAGING | null {
  if (sessions.some((s) => s.command?.repo === repo && MOVES_STAGING.has(s.command.id) && !s.ranAt)) return WAITING_FOR_STAGING;
  return null;
}

/**
 * The queued requests in `repo` that server/autoStart.ts may Send now, oldest
 * first: it starts on its own (`startsOnItsOwn`) — named or not, since Send
 * names an unnamed one after its task (`worktreeNameFor`) — nothing queued ahead of it (`waitingOnQueued`), no task off the queue it waits on unfinished (`dependsOnTasks`), and every
 * worktree it depends on done (`waitingOn`) — and no Claude-button session in
 * one of those still short of its prompt. That last is what `waitingOn` cannot
 * see: a dependency sent a moment ago has a worktree level with staging and
 * clean, because its Claude has not written anything yet. None at all while
 * auto-fix works on a failed Deploy (`fixing`, `WAITING_FOR_FIX`), nor while a
 * merge moves staging (`repoHold`), and not one whose own
 * worktree has a Claude at work in it already (`ownClaudeBusy`), nor one to
 * branch at a branch an auto-fix is still under way on (`fixes`, `fixOn`). `unmerged`
 * is the graph's (`GitGraph.unmerged`), for a dependency removed unmerged;
 * `prompts`, the worktrees' queued prompts, which a request may wait on too.
 */
export function startPlan(
  queue: readonly QueuedClaude[],
  repo: string,
  worktrees: readonly GitWorktree[] | undefined,
  sessions: readonly TmuxSession[],
  atReview: AtReview = true,
  fixing: string | null = null,
  unmerged: readonly string[] = [],
  prompts: readonly { id: string }[] = [],
  now = Date.now(),
  fixes: readonly FixOut[] = [],
): QueuedClaude[] {
  if (fixing || repoHold(sessions, repo)) return [];
  const working = (tree: string): boolean =>
    sessionsIn(sessions, repo, tree).some((s) => (s.taskId || s.claude) && s.claude?.state !== "idle");
  return queue.filter(
    (q) =>
      q.repo === repo &&
      startsOnItsOwn(q) &&
      !waitingOnQueued(q, [...queue, ...prompts]).length &&
      !q.dependsOnTasks?.length &&
      !waitingOn(q.dependsOn, worktrees, q.worktree, q.dependsOnStatus, atReviewOn(atReview, queuedBase(q)), unmerged).length &&
      !q.dependsOn.some((d) => d !== q.worktree && working(d)) &&
      !ownClaudeBusy(q, sessions, now) &&
      !fixOn(fixes, queuedBase(q), q.worktree),
  );
}

/** The branch a queued request's new worktree is made at: its task's branch, else staging. */
export const queuedBase = (q: Pick<QueuedClaude, "branch">): string => mergeBranchOf(q.branch) ?? STAGING_BRANCH;

/** The Claudes at work: a Claude button's session whose Claude is not yet
 *  `idle` (one just started has no state yet), or any Claude working or waiting.
 *  An idle one waits for a person, and closes itself after `IDLE_CLOSE_MS`. What
 *  the Global settings page's "Parallel Claudes" counts (server/claudeLimit.ts). */
export function busyClaudes(sessions: readonly TmuxSession[]): TmuxSession[] {
  return sessions.filter((s) => (s.taskId || s.claude) && s.claude?.state !== "idle");
}

/** The highest limit the Global settings take: well past what the box could run. */
export const CLAUDE_LIMIT_MAX = 50;

/** Room for another Claude, at most `max` at once; null is no limit. `starting`:
 *  starts asked for that the list does not show yet. */
export const claudeRoom = (sessions: readonly TmuxSession[], max: number | null, starting = 0): boolean =>
  max === null || busyClaudes(sessions).length + starting < max;

/** The Global settings' "Parallel Claudes" (server/claudeLimit.ts); each null is no limit.
 *  `minFreeMb`: RAM that must be available to start one. `maxCpu`: the CPU's
 *  busy share over the last minute, 0..100, past which none starts. */
export interface ClaudeLimit {
  max: number | null;
  minFreeMb: number | null;
  maxCpu: number | null;
}

/** The highest free-RAM floor the Global settings take, in MB. */
export const CLAUDE_MIN_FREE_MB_MAX = 1_048_576;

/** What a Claude just started is reckoned to take before the RAM reading shows
 *  it: Claude Code settles at about this in its first seconds. */
export const CLAUDE_START_MB = 300;

/** The box as `claudeRoomProblem()` reads it. `cpu` null: no reading yet. */
export interface MachineReading {
  freeMb: number;
  cpu: number | null;
}

/** Which of the Global settings' "Parallel Claudes" limits hold another Claude
 *  back now, each with why (null: that one does not). The Claude strip draws
 *  them red beside the usage state. */
export interface ClaudeRoomBlocks {
  claudes: string | null;
  ram: string | null;
  cpu: string | null;
}

/**
 * Every limit that holds another Claude back now (`claudeRoomProblem()` names
 * the first). `starting`: starts in flight, which the list does not show yet,
 * each a Claude at work. `settling`: those and any started so lately the RAM
 * reading may not show them, each `CLAUDE_START_MB` taken. No reading, no RAM or
 * CPU limit: a box that cannot be read is not held back.
 */
export function claudeRoomBlocks(
  sessions: readonly TmuxSession[],
  limit: ClaudeLimit,
  machine: MachineReading | null,
  starting = 0,
  settling = starting,
): ClaudeRoomBlocks {
  const out: ClaudeRoomBlocks = { claudes: null, ram: null, cpu: null };
  if (!claudeRoom(sessions, limit.max, starting)) {
    out.claudes = `${limit.max} Claude${limit.max === 1 ? " is" : "s are"} already at work, the most the global settings allow`;
  }
  if (machine && limit.minFreeMb !== null) {
    const free = Math.round(machine.freeMb - settling * CLAUDE_START_MB);
    if (free < limit.minFreeMb) {
      out.ram = `only ${Math.max(0, free)} MB of RAM free${settling ? " once the Claudes just started settle" : ""}, under the ${limit.minFreeMb} MB the global settings want before another Claude starts`;
    }
  }
  if (machine?.cpu != null && limit.maxCpu !== null && machine.cpu >= limit.maxCpu) {
    out.cpu = `the CPU is ${Math.round(machine.cpu)}% busy, at or over the ${limit.maxCpu}% the global settings allow a new Claude to start at`;
  }
  return out;
}

/** Why another Claude may not start now, or null when it may: the first of `claudeRoomBlocks()`. */
export function claudeRoomProblem(
  sessions: readonly TmuxSession[],
  limit: ClaudeLimit,
  machine: MachineReading | null,
  starting = 0,
  settling = starting,
): string | null {
  const b = claudeRoomBlocks(sessions, limit, machine, starting, settling);
  return b.claudes ?? b.ram ?? b.cpu;
}

/** Why another Claude may not start in `repo` by its own "Max Claudes"
 *  (`RepoSettings.maxClaudes`), or null: `max` null is no limit. `starting`:
 *  starts in that repo the list does not show yet. */
export function repoClaudeRoomProblem(sessions: readonly TmuxSession[], repo: string, max: number | null, starting = 0): string | null {
  if (max === null) return null;
  const busy = busyClaudes(sessions).filter((s) => s.repo === repo).length;
  return busy + starting < max ? null : `${max} Claude${max === 1 ? " is" : "s are"} already at work in ${repo}, the most its settings allow`;
}

/** A worktree pill's queued prompt (server/worktreePrompts.ts), waiting for
 *  room under the Global settings' Parallel Claudes. */
export interface QueuedPrompt {
  id: string;
  repo: string;
  worktree: string;
  prompt: string;
  createdAt: number;
  /** How it starts, as a queued Claude's: `ready` once there is room, `under` /
   *  `wastage` by the plan usage too (`usageSends`), `manual` waits until one of
   *  those is picked. */
  startMode: StartMode;
  /** As a queued Claude's (`startModeOf`), from its worktree's task — that one
   *  too, if it is queued again — and every task above it. */
  ancestorMode?: AncestorMode | null;
  /** As a queued Claude's: its repo's start limit. */
  repoMode?: StartMode | null;
  /** What it waits for, as a queued Claude does: worktrees (`waitingOn`) and
   *  queued requests or prompts, by id (`waitingOnQueued`). Sending it hands its
   *  worktree on to whatever waits on it. */
  dependsOn: string[];
  dependsOnQueued: string[];
  dependsOnStatus: Record<string, Status>;
  /** As a queued Claude's: tasks off the queue it waits for, until finished. */
  dependsOnTasks?: TaskWait[];
  /** Typed at its Claude, and listed until that is over (`promptProgress`):
   *  till then nothing else is sent to the worktree, and what waits on it waits. */
  sentAt: number | null;
  /** Its turn has come and its worktree is being caught up with staging first
   *  (server/promptQueue.ts): it is sent as soon as that ends. */
  catchingUp?: boolean;
  /** Why its last send did not go (its Claude not at its prompt, the bridge
   *  down…): still queued, and tried again in a few minutes. */
  failed?: string;
  /** Queued from the Chat in a worktree that had gone: the `worktree/…` tag
   *  it is spawned back from (with this prompt) when it is sent, as the tag's
   *  Spawn worktree. Null: a worktree gone by then takes the prompt with it. */
  tag: string | null;
  /** On an Under prompt, as on a queued Claude: what holds it (`underHeldBy`). */
  underHeldBy?: UnderHolder | null;
  /** Its worktree's task (`worktree_tasks`), where the reader may see it; null
   *  when none is recorded. A prompt has no task of its own: this is what it
   *  files under, and whose urgency it goes by. */
  worktreeTaskId?: string | null;
  /** That task's urgency: prompts to different worktrees go most urgent first
   *  (server/promptQueue.ts), as queued Claudes do. Absent is URGENCY_MIN. */
  urgency?: number;
}

/** A sent prompt's Claude has this long for its hooks to say it is working. */
export const PROMPT_SETTLE_MS = 15_000;

/**
 * Where a sent prompt is: its Claude still `working`, or `waiting` on a
 * question; its work `dirty` (not committed) or `unstaged` (commits staging
 * lacks); `settling` (just sent); `gone` (the worktree is); else `done`. Only
 * `done` and `gone` let the worktree's next prompt go and hand its worktree on
 * to what waited on it. `tree` undefined: the graph does not list it.
 */
export type PromptProgress = "settling" | "working" | "waiting" | "dirty" | "unstaged" | "done" | "gone";
export function promptProgress(
  p: Pick<QueuedPrompt, "repo" | "worktree" | "sentAt">,
  sessions: readonly Pick<TmuxSession, "repo" | "worktree" | "claude">[],
  tree: Pick<GitWorktree, "dirty" | "ahead"> | undefined,
  now: number,
): PromptProgress {
  const there = sessions.filter((s) => s.repo === p.repo && s.worktree === p.worktree && s.claude);
  if (there.some((s) => s.claude?.state === "waiting")) return "waiting";
  if (there.some((s) => s.claude?.state === "working")) return "working";
  if (now - (p.sentAt ?? now) < PROMPT_SETTLE_MS) return "settling";
  if (!tree) return "gone";
  if (tree.dirty !== false) return "dirty";
  if (tree.ahead !== 0) return "unstaged";
  return "done";
}

/** The longest a queued prompt may be, and what the bridge's `t: "type"` takes: as long as
 *  a Claude's first prompt, since the bridge pastes it rather than typing it as a command line. */
export const PROMPT_MAX_CHARS = CLAUDE_PROMPT_MAX;

/**
 * What a session may be called. tmux itself turns `.` and `:` into `_` (they
 * are target syntax), so a name with them would come back as a different one;
 * everything else outside this set is only ever a quoting accident.
 */
export const TMUX_NAME = /^[A-Za-z0-9_-]{1,64}$/;

export const TMUX_MIN_COLS = 10;
export const TMUX_MAX_COLS = 500;
export const TMUX_MIN_ROWS = 3;
export const TMUX_MAX_ROWS = 200;

/** A terminal size from anywhere untrusted, clamped rather than refused. */
export function clampSize(cols: unknown, rows: unknown): { cols: number; rows: number } {
  const clamp = (v: unknown, lo: number, hi: number, fallback: number): number => {
    const n = Math.floor(Number(v));
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
  };
  return {
    cols: clamp(cols, TMUX_MIN_COLS, TMUX_MAX_COLS, 80),
    rows: clamp(rows, TMUX_MIN_ROWS, TMUX_MAX_ROWS, 24),
  };
}

/** How a queued Claude plans: not at all, plans and waits for a person's
 *  go-ahead (`plan`), or plans and goes on in auto mode by itself (`planAuto`).
 *  One choice in the UI, two flags on the wire and in the database. */
export const PLAN_MODES = ["off", "plan", "auto"] as const;
export type PlanMode = (typeof PLAN_MODES)[number];
export const planModeOf = (q: { plan?: boolean; planAuto?: boolean }): PlanMode => (q.plan ? (q.planAuto ? "auto" : "plan") : "off");
export const planFields = (mode: PlanMode): { plan: boolean; planAuto: boolean } => ({ plan: mode !== "off", planAuto: mode === "auto" });

/**
 * An interactive Claude Code in a new session, from a task's Claude button.
 * `name` is what todos would like it called; the bridge adds `-2`, `-3`… if
 * that is taken, and answers with the name it used in `session`.
 *
 * It runs in the claude user's checkout of `repo` (cloned on first use), on
 * whatever that checkout has out — or, with `worktree`, in the worktree Claude
 * Code makes for itself under it (`claude --worktree`).
 */
export interface ClaudeSessionRequest {
  t: "claude";
  id: number;
  name: string;
  taskId: string;
  repo: string;
  worktree: string | null;
  prompt: string;
  /** Absent from a server older than the bridge: then it is `default`, no --model. */
  model?: ClaudeModel;
  /** Start it in plan mode (`--permission-mode plan`). Absent is false. */
  plan?: boolean;
  /** With `plan`: approve its plan by a hook and go on in auto mode
   *  (`claudeStateHooks`' `planAuto`). Absent is false. */
  planAuto?: boolean;
  /** A queued request: its worktree, when new, branches at `staging` rather
   *  than main (`claude --worktree` can only do main). Absent is false. */
  fromStaging?: boolean;
  /** With `fromStaging`: the queued task's branch, which a new worktree branches
   *  at instead of staging when it exists (`mergeBranchProblem`). */
  base?: string;
  /** The repo's create hook (`WorktreeHooks.create`), run in the session
   *  before Claude when the worktree is new. Added by todos's `request()`. */
  createHook?: string;
  /** To a WORKER only: where its new worktree starts. This box pushed `sha` (its
   *  staging, or the kept branch) to `refs/todos/dispatch/<worktree>` on the git
   *  server (`dispatch-base`); the worker clones the repo from its own remote
   *  with `folder` (shared/repoFolders.ts) and makes the worktree there. */
  dispatch?: WorkerDispatch;
}

/** See `ClaudeSessionRequest.dispatch`. */
export interface WorkerDispatch {
  sha: string;
  folder: string;
  defaultFolder: string;
}

/** Where a worker's work travels through the git server, out of sight of the
 *  git panel (it logs branches and remotes only): the base it starts from, its
 *  commits on the way home, and its conversations packed as a commit. */
export const DISPATCH_REF = "refs/todos/dispatch/";
export const LANDING_REF = "refs/todos/landing/";
export const TRANSCRIPTS_REF = "refs/todos/transcripts/";

/** What a worker hands back (`hand-back`): its worktree's tip on `LANDING_REF`,
 *  and its conversations, a commit of its projects dir, on `TRANSCRIPTS_REF`
 *  (null: none), with each file's mtime (relative path → ms), put back as they
 *  were so this box's usage count does not take them for new spend. `fromDir`:
 *  the worktree's path on the worker, which their `cwd` lines name. */
export interface HandBack {
  head: string;
  transcripts: string | null;
  mtimes: Record<string, number>;
  fromDir: string;
}

/**
 * One of a repo's commands from the Repos view (shared/repoSettings.ts), run by
 * bash in a new session in the claude user's checkout of `repo`, with
 * `message` in `$MESSAGE`. The session waits for Enter once the command ends,
 * so its output is there to read. Answered, like `claude`, with `session`.
 */
export interface RunCommandRequest {
  t: "run";
  id: number;
  name: string;
  repo: string;
  command: string;
  message: string;
  /** Run in this Claude Code worktree of the checkout, not the checkout itself.
   *  Optional so an older server's requests still mean the checkout. */
  worktree?: string | null;
  /** The command's id (`RepoCommand.id`), stamped on the session as `TmuxSession.command`.
   *  Optional so an older server's requests still run. */
  commandId?: string;
}

/**
 * A command typed under a terminal, run like a repo's command (a new session
 * that holds its output until Enter) but in whatever directory the session
 * named `in` is sitting in — the bridge reads it off the pane, so no path ever
 * comes from the browser. Answered with `session`.
 */
export interface ExecRequest {
  t: "exec";
  id: number;
  in: string;
  command: string;
}

/** A task id as server/ids.ts makes them, before it goes near a tmux argv. */
export const TASK_ID = /^[a-z0-9]{1,32}$/;

/** A repo name as todos hands it out (server/repos.ts): never a path. */
export const REPO_NAME = /^[A-Za-z0-9_][A-Za-z0-9._-]{0,99}$/;

/**
 * The sessions working in `repo`'s checkout (`worktree` null) or in its
 * worktree `worktree`, in the order `sessions` has them.
 */
export function sessionsIn<S extends Pick<TmuxSession, "repo" | "worktree">>(
  sessions: readonly S[],
  repo: string,
  worktree: string | null,
): S[] {
  return sessions.filter((s) => s.repo === repo && s.worktree === worktree);
}

/** How long a Claude button's session may take to show its Claude: the repo's
 *  create hook runs in it first. */
export const CLAUDE_STARTING_MS = 3 * 60_000;

/**
 * ONE CLAUDE SESSION PER TREE. The sessions in `repo`'s worktree `worktree`
 * (null: its checkout) that are a Claude's: one with a Claude running, or a
 * Claude button's started so lately that its Claude is not up yet. A shell a
 * Claude left behind when it exited is nobody's, and neither is a terminal or
 * a command's session. Every start of a Claude in a tree goes by these
 * (`oneClaudeIn()`, server/tmux.ts): picking a conversation back up goes to the
 * one there, and a new one takes its place once it is idle — never beside it.
 */
export function claudesIn<S extends Pick<TmuxSession, "repo" | "worktree" | "claude" | "taskId" | "createdAt" | "command">>(
  sessions: readonly S[],
  repo: string,
  worktree: string | null,
  now: number,
): S[] {
  return sessionsIn(sessions, repo, worktree).filter((s) => s.claude || (s.taskId && !s.command && now - s.createdAt < CLAUDE_STARTING_MS));
}

/** One of `claudesIn()` that a new Claude may not replace: mid-turn, stopped on
 *  a question, or not yet at its prompt. An idle one waits on a person, and
 *  its conversation stays on disk. */
export const claudeAtWork = (s: Pick<TmuxSession, "claude">): boolean => s.claude?.state !== "idle";

/**
 * The worktree a queued request's Claude would start in, while a Claude there
 * is at work — a Send is refused until it stops (one Claude per worktree), so
 * nothing sends it on its own meanwhile. The name it was given, else the one
 * Send derives from its task's title; a derived name is only the same worktree
 * while it is this task's (another's is numbered on, `freeWorktreeFor`).
 */
export function ownClaudeBusy(q: Pick<QueuedClaude, "repo" | "worktree" | "taskId" | "taskTitle">, sessions: readonly TmuxSession[], now: number): string | null {
  if (!q.repo) return null;
  const derived = (q.taskTitle && worktreeNameFor(q.taskTitle)) || `task-${q.taskId}`;
  const name = q.worktree ?? derived;
  const given = name !== derived;
  return claudesIn(sessions, q.repo, name, now).some((s) => claudeAtWork(s) && (given || s.taskId === q.taskId)) ? name : null;
}

/** What a command id stamped on a session may be: a saved one's or a fixed, hyphenated one's. */
export const COMMAND_ID = /^[a-z0-9-]{1,32}$/;

/**
 * The Deploy of `repo` still running, if one is: a session its button started
 * whose command has not finished. Killing the session ends it, as it ends the
 * deploy. While it runs, what would move main waits (`heldByDeploy`).
 */
export function runningDeploy<S extends Pick<TmuxSession, "command" | "ranAt">>(sessions: readonly S[], repo: string): S | undefined {
  return sessions.find((s) => s.command?.repo === repo && s.command.id === "deploy" && !s.ranAt);
}

/**
 * The running Deploy of `repo` that `commandId`, run in `worktree` (null: the
 * checkout), has to wait for, if any (`heldByDeploy`). A merge into staging —
 * the only thing held that runs in a worktree — waits only until the Deploy
 * has merged staging into main (`stagingMerged`): staging then moves on, and
 * what lands on it goes out with the next deploy.
 */
export function deployHolding<S extends Pick<TmuxSession, "command" | "ranAt" | "stagingMerged">>(
  sessions: readonly S[],
  repo: string,
  commandId: string,
  worktree: string | null,
): S | undefined {
  if (!heldByDeploy(commandId, worktree)) return undefined;
  const running = runningDeploy(sessions, repo);
  return running && !(worktree && running.stagingMerged) ? running : undefined;
}

/** A Claude worktree with work left uncommitted: what server/autoCommit.ts commits. */
export interface CommitCandidate {
  worktree: string;
  /** The Claude session's task: its title is the message. Null for an
   *  auto-fix worktree, which is for no task. */
  taskId: string | null;
  /** Changes once its Claude has written anything more. One try per turn, so
   *  neither a list read before a commit showed up nor a commit that failed
   *  is tried again until there is something new to try it on. */
  turn: string;
}

/** A worktree session whose work has not reached main: commits main lacks, or
 *  uncommitted changes. Unknown (an older bridge, git failed) is not unmerged. */
export function unmerged(s: Pick<TmuxSession, "worktree" | "worktreeDirty" | "worktreeInMain">): boolean {
  return !!s.worktree && (s.worktreeDirty || s.worktreeInMain === false);
}

/**
 * The session list's band within a repo, lowest first: a Claude mid-turn or
 * stopped on a question, then work not yet in main, then the rest.
 */
export function sessionRank(s: Pick<TmuxSession, "worktree" | "worktreeDirty" | "worktreeInMain" | "claude">): number {
  if (s.claude?.state === "working" || s.claude?.state === "waiting") return 0;
  return unmerged(s) ? 1 : 2;
}

/**
 * The worktrees of `repo` to commit now: each with a session a Claude button
 * started for a task, with changes, whose Claude has stopped at its prompt —
 * not in a turn, not on a permission question. Not while another Claude in
 * that worktree is at work, nor while a command runs there (a merge, a Delete,
 * the last commit). A Claude started by hand has no task, so it has no message
 * and is left alone; a resumed one carries its worktree's recorded task. An
 * auto-fix worktree (`isFixWorktree`) has none either, but is taken, with
 * `taskId` null: its message is the fix's own prompt.
 */
export function commitPlan(sessions: readonly TmuxSession[], repo: string): CommitCandidate[] {
  const out: CommitCandidate[] = [];
  for (const s of sessions) {
    if (s.repo !== repo || !s.worktree || !s.worktreeDirty || s.claude?.state !== "idle") continue;
    if (!s.taskId && !isFixWorktree(s.worktree)) continue;
    if (out.some((c) => c.worktree === s.worktree)) continue;
    const there = sessionsIn(sessions, repo, s.worktree);
    if (there.some((t) => t.claude?.state === "working" || t.claude?.state === "waiting" || (t.command && !t.ranAt))) continue;
    out.push({ worktree: s.worktree, taskId: s.taskId ?? null, turn: `${s.name}:${s.claude.outputTokens}` });
  }
  return out;
}

/** A worktree a task set done takes with it (server/doneCleanup.ts). */
export interface DoneWorktree {
  repo: string;
  worktree: string;
}

/**
 * What setting `taskId` done closes: each worktree that is the task's — a
 * session in it carries the task, or its Claude button was started for it
 * (`recorded`, server/worktreeTasks.ts) — removed as the pill's Delete does,
 * and every other session carrying the task killed. A worktree recorded for
 * another task, or with a session on another task in it, is that task's too:
 * it stays, and only this task's sessions there go. Whether each worktree still
 * exists is the caller's to ask.
 */
export function donePlan(
  taskId: string,
  sessions: readonly Pick<TmuxSession, "name" | "repo" | "worktree" | "taskId">[],
  recorded: readonly (DoneWorktree & { taskId: string })[],
): { kill: string[]; worktrees: DoneWorktree[] } {
  const key = (repo: string, worktree: string): string => `${repo}/${worktree}`;
  const owner = new Map(recorded.map((r) => [key(r.repo, r.worktree), r.taskId]));
  const trees = new Map<string, DoneWorktree>();
  for (const r of recorded) if (r.taskId === taskId) trees.set(key(r.repo, r.worktree), { repo: r.repo, worktree: r.worktree });
  for (const s of sessions) if (s.taskId === taskId && s.repo && s.worktree) trees.set(key(s.repo, s.worktree), { repo: s.repo, worktree: s.worktree });
  const worktrees = [...trees.values()].filter(({ repo, worktree }) => {
    const recordedFor = owner.get(key(repo, worktree));
    if (recordedFor && recordedFor !== taskId) return false;
    return !sessionsIn(sessions, repo, worktree).some((s) => s.taskId && s.taskId !== taskId);
  });
  const removed = new Set(worktrees.map((w) => key(w.repo, w.worktree)));
  const kill = sessions
    .filter((s) => s.taskId === taskId && !(s.repo && s.worktree && removed.has(key(s.repo, s.worktree))))
    .map((s) => s.name);
  return { kill, worktrees };
}

/** Whether a session must be left to finish before its worktree goes: a Claude
 *  mid-turn or on a question, or a command a button started still running. */
export const midWork = (s: Pick<TmuxSession, "claude" | "command" | "ranAt">): boolean =>
  s.claude?.state === "working" || s.claude?.state === "waiting" || (!!s.command && !s.ranAt);

/** A task or worktree name as a session name: `TMUX_NAME` has no dots. */
export function sessionName(text: string): string {
  return text.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 56) || "claude";
}

/** todos → bridge. `ch` names one attached terminal; `id` one request. */
export type ToBridge =
  | { t: "list"; id: number }
  /** A shell, in ~ — or, with `repo`, in the claude user's checkout of it, or
   *  in its worktree `worktree`; neither is ever made. Answered with `session`. */
  | { t: "new"; id: number; name: string | null; repo?: string | null; worktree?: string | null }
  | { t: "kill"; id: number; name: string }
  /** `text` typed at session `name`'s prompt, then Enter a moment later — as a
   *  person would, since Claude Code takes text and Enter in one read as a
   *  paste. Auto-merge hands a conflict to a worktree's Claude this way. */
  | { t: "type"; id: number; name: string; text: string }
  /** An answer to the dialog a Claude is waiting on (`Transcript.dialog`), from
   *  the Chat: `key` pressed in `pane` — an option's number, or Right to a
   *  question's next tab. Only while the dialog there still has that option
   *  under that number (`label`; the tabs line for Right), so an answer to a
   *  dialog that has gone is refused rather than typed at whatever is there.
   *  With `text`, the option asks for words (`asksForText`): the cursor is put
   *  on it, `text` typed there, then Enter — none for a multi-select question,
   *  where typing ticks it and → moves on. */
  | { t: "answer"; id: number; pane: string; key: string; label: string; text?: string }
  /** The Chat's mode select: Shift+Tab pressed in the Claude's `pane` until its
   *  footer names `mode` (`paneMode`), as a person cycling there would. Refused
   *  when a whole turn of the cycle never shows it, the mode left as it was.
   *  Answered with the `mode` it ended in. */
  | { t: "mode"; id: number; pane: string; mode: ClaudeMode }
  /** The Chat's Stop, and its dialog's Cancel: Esc in the Claude's `pane`, only
   *  while it is mid-turn or showing a dialog — Esc at an idle prompt clears
   *  what is typed there, and twice opens its rewind menu. */
  | { t: "interrupt"; id: number; pane: string }
  | ClaudeSessionRequest
  | RunCommandRequest
  | ExecRequest
  /** The repos hosted on this box, for the Repos view: the bridge can see
   *  /home/git/repos, todos cannot. With `tokens`, each one's Claude spend too
   *  (`claudeTokens`), which reads every transcript: only the view asks. */
  | { t: "repos"; id: number; tokens?: boolean }
  /** A new empty repo on the git server, by the git user's `newrepo` over
   *  ssh. Answered as `repos`, with it in the list. */
  | { t: "new-repo"; id: number; repo: string; folder?: string | null }
  /** A repo's commit graph for the tmux view's git panel (shared/gitGraph.ts),
   *  read in the claude user's checkout — after a `git fetch` when `fetch`.
   *  `limit`: that many commits, not GIT_LOG_LIMIT (the panel's "Load more"). */
  | { t: "git"; id: number; repo: string; fetch: boolean; limit?: number }
  /** What Commit would commit in the claude user's checkout of `repo` — or in
   *  its worktree `worktree` (shared/diff.ts) — or, with `commit`, what that
   *  commit changed against its first parent (the git panel's "Show diff"). */
  | { t: "diff"; id: number; repo: string; worktree?: string | null; commit?: string | null }
  /** The file explorer (shared/files.ts), in the checkout of `repo` or its
   *  worktree `worktree`: a directory's entries, a file's text, or a save —
   *  refused when the file's mtime is no longer `mtimeMs` (null: a new file,
   *  refused if one is there). Answered with `listing`, then `file`. */
  | { t: "ls"; id: number; repo: string; worktree: string | null; path: string }
  /** The stats view's Disk breakdown: `du` of `path`, a level deep, as the
   *  claude user (runner/disk.ts). Answered with `disk`. */
  | { t: "disk"; id: number; path: string }
  | { t: "read-file"; id: number; repo: string; worktree: string | null; path: string }
  /** The Chat (shared/transcript.ts): a Claude conversation as text — the one
   *  the Claude in session `name` is having (else the newest where it works),
   *  or with `repo` the newest in its checkout or worktree `worktree`, which
   *  need not be out any more. `conversation` picks another filed there, and
   *  `from` carries on a read of it. Answered with `transcript`. */
  | { t: "transcript"; id: number; name?: string; repo?: string; worktree?: string | null; conversation?: string | null; from?: number | null; contextOnly?: boolean }
  | { t: "write-file"; id: number; repo: string; worktree: string | null; path: string; text: string; mtimeMs: number | null }
  /** A file added in the Chat: `data` (base64) kept in worktree `worktree`'s
   *  `UPLOAD_DIR` (shared/files.ts), never the checkout's, under a name made
   *  from `name`. Answered with `path`, the file's in that tree. */
  | { t: "upload"; id: number; repo: string; worktree: string; name: string; data: string }
  /** `claude -c` — the last conversation there, picked back up — in a new session in
   *  the claude user's checkout of `repo`, or its worktree `worktree`; neither
   *  is ever made. `taskId` is the task the worktree was started for
   *  (`worktree_tasks`), carried as a Claude button's session carries its own.
   *  Answered with `session`. */
  | { t: "resume"; id: number; repo: string; worktree: string | null; taskId?: string; fresh?: true; prompt?: string }
  /** The staging pill's "Claude": a new worktree whose branch starts at
   *  `staging` (`claude --worktree` would branch from main), and an interactive
   *  Claude Code in it, for no task. The bridge names it. Answered with `session`.
   *  With `fix` — auto-fix, after a failed Deploy — the worktree is `fix-<n>`,
   *  made where the checkout is when there is no staging, and the Claude is
   *  given `AUTO_FIX_PROMPT`. `base: "main"` — the main pill's Test — branches
   *  it at main instead of staging. `failed` names the failed Deploy's session,
   *  whose output (`deployFailure`) goes into the fix's prompt; `fixPrompt`, the
   *  repo's own (`RepoPrompts.autoFix`), stands in for `AUTO_FIX_PROMPT`. `prompt` — an
   *  Automation Claude node's — is passed as argv, and like `fix` falls back to
   *  the checkout's HEAD in a repo with no staging. */
  | { t: "staging-claude"; id: number; repo: string; fix?: boolean; base?: string; failed?: string; fixPrompt?: string; prompt?: string; createHook?: string }
  /** A `worktree/…` tag's "Spawn worktree": that worktree made again where the
   *  tag is, under the same name, and `claude -c` in it, carrying `taskId` as
   *  `resume` does, and given `prompt` (a queued prompt's, argv) as `resume` is.
   *  Answered with `session`. */
  | { t: "spawn-worktree"; id: number; repo: string; tag: string; taskId?: string; createHook?: string; prompt?: string }
  /** The Chat's Revise plan: conversation `conversation` (had in worktree
   *  `worktree`, gone or not) forked just before the plan whose call is
   *  `toolId` was approved, in a NEW worktree `newWorktree` made at staging
   *  with the commits that carried the plan out reverted, and Claude resumed
   *  there in plan mode (runner/revisePlan.ts). Answered with `session`, and
   *  `reverted` (`<short hash> <subject>`, each) and `revertFailed`. */
  | { t: "revise-plan"; id: number; repo: string; worktree: string; newWorktree: string; conversation: string; toolId: string; taskId?: string; createHook?: string }
  /** Before a worker is sent a queued Claude: the sha its worktree starts at —
   *  the kept `worktree-<name>` branch, else `base` (`worktreeBase`), else
   *  staging, else HEAD — pushed to `DISPATCH_REF<worktree>`. Refused while the
   *  worktree is out here. Answered with `sha`. */
  | { t: "dispatch-base"; id: number; repo: string; worktree: string; base?: string }
  /** A worker's worktree come home (`HandBack`): fetched from `LANDING_REF`, made
   *  a worktree here on `worktree-<name>` as a Claude button's is (the create
   *  hook, `node_modules`), its conversations unpacked under this tree's projects
   *  dir with their `cwd` moved here. Idempotent. Answered with `path`. */
  | ({ t: "land-worktree"; id: number; repo: string; worktree: string; createHook?: string } & HandBack)
  /** On a WORKER: its sessions in the worktree killed (all must be idle, nobody
   *  attached), what is uncommitted committed as `message` (refused, 79, on
   *  conflict markers, as auto-commit is), pushed to `LANDING_REF`, and its
   *  conversations to `TRANSCRIPTS_REF`. Answered with `handBack`. */
  | { t: "hand-back"; id: number; repo: string; worktree: string; message: string }
  /** On a WORKER, once landed: its copy of the worktree removed, the branch with it. */
  | { t: "drop-worktree"; id: number; repo: string; worktree: string }
  /** An Automation Claude node's message: one `claude -p`, run to its end (`ClaudePrintRequest`).
   *  Answered with `print`. */
  | ({ t: "print"; id: number } & ClaudePrintRequest)
  | { t: "open"; ch: number; name: string; cols: number; rows: number }
  | { t: "in"; ch: number; data: string }
  | { t: "resize"; ch: number; cols: number; rows: number }
  /** Through tmux's own history, negative = back. See `FromTerminal`. */
  | { t: "scroll"; ch: number; lines: number }
  | { t: "close"; ch: number };

/** What a Claude node's `claude -p` may use: nothing, reading the tree it runs in, or editing it too. */
export const PRINT_TOOLS = ["none", "read", "edit"] as const;
export type PrintTools = (typeof PRINT_TOOLS)[number];
/** A `claude -p` may run this long at most, whatever a node asks. */
export const PRINT_MAX_MS = 60 * 60_000;

/**
 * One `claude -p` (runner/print.ts): run in `worktree` of `repo`, else `repo`'s
 * checkout, else no repo at all (a temporary directory). `edit` only with a
 * worktree named — Claude never works in the checkout. The prompt goes in on
 * stdin, so it is neither argv's to limit nor `ps`'s to show.
 */
export interface ClaudePrintRequest {
  repo: string | null;
  worktree: string | null;
  prompt: string;
  model: ClaudeModel;
  tools: PrintTools;
  timeoutMs: number;
}

/** What a `claude -p` answered, from its `--output-format json`. */
export interface ClaudePrint {
  /** Its final message; null when it failed. */
  result: string | null;
  /** Why it failed: Claude Code's own error, a timeout, a crash. Null on success. */
  error: string | null;
  sessionId: string | null;
  costUsd: number | null;
  /** Input counts the cache reads and writes too, as the plan does. */
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number;
}

/** bridge → todos. Terminal output is base64: it is bytes, not text, and a
 *  UTF-8 character can straddle two reads. */
export type FromBridge =
  | {
      t: "res";
      id: number;
      sessions?: TmuxSession[];
      error?: string;
      session?: string;
      repos?: string[];
      /** With `repos`: each repo's code-team folder (shared/repoFolders.ts). Absent from an older bridge. */
      repoFolders?: Record<string, string>;
      /** With `repos`: the folder `RUNNER_HOSTED_REPOS_DIR` names, which a team with none sees. */
      defaultFolder?: string;
      /** With `repos`: the deploy script for each repo that has one on this box. */
      deployScripts?: Record<string, string>;
      /** With `repos`: those whose deploy script takes `--quick` (`QUICK_DEPLOY_MARK`). */
      quickDeploys?: string[];
      /** Each repo's branch deploy scripts by `branchScriptSlug` (runner/tmux.ts `branchDeployScripts`). */
      branchDeploys?: Record<string, Record<string, string>>;
      /** With `repos`, when asked (and always for `new-repo`): output tokens Claude
       *  Code has written in each repo, by name, over every conversation on disk
       *  filed under its checkout or a worktree of it. None is left out. */
      claudeTokens?: Record<string, number>;
      /** For `git`: the repo's commit graph. */
      graph?: GitGraph;
      /** For `diff`: the checkout's uncommitted changes. */
      diff?: RepoDiff;
      /** For `ls`. */
      listing?: DirListing;
      /** For `read-file`, and `write-file` (as saved, `text` null). */
      file?: RepoFile;
      /** For `disk`. */
      disk?: DiskUsage;
      /** For `transcript`. */
      transcript?: Transcript;
      /** For `mode`: the mode the Claude is now in. */
      mode?: ClaudeMode;
      /** For `land-worktree`: where it is. */
      path?: string;
      /** For `print`: what the `claude -p` said. */
      print?: ClaudePrint;
      /** For `revise-plan`: the commits taken out (`<short hash> <subject>`), and
       *  whether reverting them conflicted, so they were left in. */
      reverted?: string[];
      revertFailed?: boolean;
      /** For `dispatch-base`: the sha pushed. */
      sha?: string;
      /** For `hand-back`. */
      handBack?: HandBack;
      /** From a worker, on every answer that lists its sessions: its room. */
      room?: WorkerRoom;
    }
  /** A worker's first word: what it speaks, and the worktrees it has out. */
  | { t: "hello"; protocol: number; version: string; room: WorkerRoom; worktrees: WorkerTree[] }
  | { t: "out"; ch: number; data: string }
  | { t: "exit"; ch: number; error?: string };

/** browser → todos, as text frames. Output comes back as binary frames. */
export type FromTerminal =
  | { t: "in"; data: string }
  | { t: "resize"; cols: number; rows: number }
  /** A swipe on a touch screen, in lines, negative = back through history.
   *  xterm keeps no scrollback (tmux has it), so a drag would otherwise do
   *  nothing; the bridge scrolls the pane in tmux's copy mode instead. */
  | { t: "scroll"; lines: number };
/** todos → browser, the text frames: why the terminal ended. */
export type ToTerminal = { t: "exit"; error?: string };

/**
 * Which Claude sessions began a turn since `seen` was taken (server/reviewReopen.ts):
 * each session's turn key — its time on finished turns while `working` or
 * `waiting`, which holds still through the turn and moves once it ends — against
 * the last look's. A session seen for the first time only seeds `seen`, so a turn
 * already under way when the look began is nobody's news.
 */
export function startedTurns<S extends Pick<TmuxSession, "name" | "claude">>(
  seen: ReadonlyMap<string, string>,
  sessions: readonly S[],
): { seen: Map<string, string>; started: S[] } {
  const next = new Map<string, string>();
  const started: S[] = [];
  for (const s of sessions) {
    const state = s.claude?.state;
    const key = state === "working" || state === "waiting" ? `turn:${s.claude!.workedMs}` : (state ?? "");
    next.set(s.name, key);
    const was = seen.get(s.name);
    if (state === "working" && was !== undefined && was !== key) started.push(s);
  }
  return { seen: next, started };
}

/** How long a session must have printed nothing, been sent no key and had
 *  nobody attached before `idleSessions()` closes it (server/idleClose.ts). */
export const IDLE_CLOSE_MS = 20 * 60_000;

/**
 * Which sessions have sat unwatched and silent for `IDLE_CLOSE_MS`: no output,
 * no key, and no client attached for that long (`server/idleClose.ts` kills
 * them). tmux does not say when the last client went, so `unwatched` holds when
 * each was first seen with none — a restart of todos starts the count again,
 * which only ever closes later. Never one somebody still has to act on: a
 * Claude stopped on a question, a command still running (a Deploy's session is
 * its lock), or one that failed and was left open to be read.
 */
export function idleSessions<S extends Pick<TmuxSession, "name" | "attached" | "createdAt" | "activityAt" | "outputAt" | "claude" | "command" | "ranAt" | "ranStatus">>(
  unwatched: ReadonlyMap<string, number>,
  sessions: readonly S[],
  now: number,
  quietMs = IDLE_CLOSE_MS,
): { unwatched: Map<string, number>; close: S[] } {
  const next = new Map<string, number>();
  const close: S[] = [];
  for (const s of sessions) {
    if (s.attached > 0) continue;
    const since = unwatched.get(s.name) ?? now;
    next.set(s.name, since);
    if (s.claude?.state === "waiting") continue;
    if (s.command && !s.ranAt) continue;
    if (s.ranAt && s.ranStatus !== 0) continue;
    if (now - Math.max(since, s.outputAt, s.activityAt, s.createdAt) >= quietMs) close.push(s);
  }
  return { unwatched: next, close };
}

/** How long an auto-fix's commits not yet merged hold its branch (`fixesOut`):
 *  `MERGE_FIX_WAIT_MS`, as long as a deploy waits on a conflict being fixed. */
export const FIX_HOLD_MS = 30 * 60_000;

/** An auto-fix worktree still at work on the branch it goes back into. */
export interface FixOut {
  worktree: string;
  /** Where auto-merge takes it: staging, or the branch a link's or a Pull's fix was made off. */
  into: string;
}

/**
 * The auto-fix worktrees (`fix-<n>`, `isFixWorktree`) of `repo` still under
 * way, each with the branch it goes into (`into`: its `fix_targets` row, else
 * staging). Under way is a Claude there not yet back at its prompt — or one
 * started under `CLAUDE_STARTING_MS` ago whose Claude is not up yet — or, with
 * auto-merge taking worktrees (`merging`), commits its branch has that its
 * target lacks, until `FIX_HOLD_MS` after the last: auto-merge has yet
 * to take them. Auto-merge sends nothing else into that branch meanwhile, and
 * nothing queued branched at it starts on its own (`fixOn`), so the fix lands
 * on the branch it was made for, and new work starts from the fixed one.
 * Without auto-merge only its Claude's turn counts: nothing takes its commits
 * on, and a person merging them is not waited for.
 */
export function fixesOut(
  graph: Pick<GitGraph, "commits" | "worktrees">,
  sessions: readonly TmuxSession[],
  repo: string,
  into: ReadonlyMap<string, string>,
  merging: boolean,
  now: number,
): FixOut[] {
  const trunk = trunkOf(graph.commits);
  const shaOf = (ref: string): string | null => graph.commits.find((c) => c.refs.includes(ref))?.hash ?? null;
  const out: FixOut[] = [];
  for (const w of graph.worktrees ?? []) {
    if (!w.name || !isFixWorktree(w.name)) continue;
    const dest = into.get(w.name) ?? STAGING_BRANCH;
    const atWork = sessionsIn(sessions, repo, w.name).some((s) => !s.command && (s.claude ? s.claude.state !== "idle" : now - s.createdAt < CLAUDE_STARTING_MS));
    let ahead = false;
    if (!atWork && merging) {
      const tip = graph.commits.find((c) => c.refs.includes(w.branch));
      const onto = shaOf(dest) ?? shaOf(STAGING_BRANCH) ?? shaOf(trunk);
      ahead = !!tip && !!onto && now - tip.at < FIX_HOLD_MS && !reaches(graph.commits, onto, tip.hash);
    }
    if (atWork || ahead) out.push({ worktree: w.name, into: dest });
  }
  return out;
}

/**
 * The auto-fix worktree (`fix-<n>`) going into `branch` (`into`, as for
 * `fixesOut`) that has already LANDED there since `sha`, a commit a run of it
 * failed at: its tip is in the branch now and was not in `sha`. A Deploy and
 * the branch's own test fail on one commit minutes apart (the test waits on
 * the box's test lock); by the time the second fails the first's fix may be
 * done and merged, so not under way, and a second Claude would fix what is
 * fixed. Null when none has, or `sha` is past the graph and cannot tell.
 */
export function fixLandedSince(graph: Pick<GitGraph, "commits" | "worktrees">, into: ReadonlyMap<string, string>, branch: string, sha: string): string | null {
  const commits = graph.commits;
  const head = commits.find((c) => c.refs.includes(branch))?.hash;
  if (!head || head === sha || !commits.some((c) => c.hash === sha)) return null;
  for (const w of graph.worktrees ?? []) {
    if (!w.name || !isFixWorktree(w.name) || (into.get(w.name) ?? STAGING_BRANCH) !== branch) continue;
    const tip = commits.find((c) => c.refs.includes(w.branch))?.hash;
    if (tip && reaches(commits, head, tip) && !reaches(commits, sha, tip)) return w.name;
  }
  return null;
}

/** The fix under way (`fixesOut`) on `branch` that something else going into it,
 *  or starting from it, waits for — never one a fix itself waits on, or two
 *  fixes into one branch would hold each other for good. */
export const fixOn = (fixes: readonly FixOut[], branch: string, worktree?: string | null): FixOut | null =>
  worktree && isFixWorktree(worktree) ? null : (fixes.find((f) => f.into === branch && f.worktree !== worktree) ?? null);
